import type { AddonItem, PricingProject, PricingSettings, WbsItem } from "./types";

export const DEFAULT_SETTINGS: PricingSettings = {
  dayRate: 1800,
  redlineRate: 1000,
  hourlyRate: 280,
  minimumHours: 4,
  monthlyFee: 32000,
  monthlyIncludedDays: 22,
  monthlyExcessDayRate: 1800,
  fixedMinimum: 3000,
  reserveClear: 0.1,
  reserveUncertain: 0.15,
  specialFactorCap: 1.6,
  warrantyDays: 30,
  validityDays: 15,
  splitDaysThreshold: 60,
  splitAmountThreshold: 100000,
  codingShare: 0.42,
  maintenanceBasicRate: 0.12,
  maintenanceBasicMinimum: 6000,
  maintenanceBasicHours: 24,
  maintenanceStandardRate: 0.18,
  maintenanceStandardMinimum: 12000,
  maintenanceStandardHours: 60,
  platformCoefficients: {
    "后端/API/数据库": 1,
    "PC管理后台": 0.9,
    "Web/H5": 1,
    "微信小程序": 1,
    "支付宝/抖音小程序": 1,
    "Android原生": 1.2,
    "iOS原生": 1.25,
    HarmonyOS: 1.3,
    "同源小程序迁移": 0.55,
    "跨端额外目标平台": 0.35,
  },
  complexityCoefficients: { 常规业务: 1, 复杂业务: 1.15, 高复杂业务: 1.3 },
  deliveryCoefficients: {
    "工期压缩20%-30%": 1.2,
    "工期压缩超过30%": 1.35,
    夜间周末紧急: 1.5,
    旧项目接手: 1.2,
    现场工作: 1.2,
  },
  quickSizeDays: { XS: 0.5, S: 1, M: 2.5, L: 6, 手动: 0 },
};

export const newId = () => crypto.randomUUID();

const seedWbs = (): WbsItem[] => [
  ["共同后端", "用户与权限", "后端/API/数据库", "M", "常规业务", "编码人日", 0],
  ["共同后端", "核心业务API", "后端/API/数据库", "M", "复杂业务", "编码人日", 0],
  ["管理后台", "基础业务管理", "PC管理后台", "M", "常规业务", "编码人日", 0],
  ["微信小程序", "核心业务流程", "微信小程序", "L", "常规业务", "编码人日", 0],
  ["Android", "会员与订单", "Android原生", "M", "常规业务", "编码人日", 0],
  ["iOS", "会员与订单", "iOS原生", "M", "常规业务", "编码人日", 0],
  ["交付", "部署与技术文档", "后端/API/数据库", "手动", "常规业务", "完整人日", 2],
].map(([module, feature, platform, quickSize, complexity, estimateBasis, inputDays]) => ({
  id: newId(),
  module: String(module),
  feature: String(feature),
  phase: quickSize === "手动" ? "部署与交付" : "开发构建",
  platform: platform as WbsItem["platform"],
  estimateBasis: estimateBasis as WbsItem["estimateBasis"],
  quickSize: quickSize as WbsItem["quickSize"],
  inputDays: Number(inputDays),
  complexity: complexity as WbsItem["complexity"],
  deliveryFactors: [],
  notes: "",
}));

const seedAddons = (): AddonItem[] => [
  ["UI设计", "专业UI设计", "客户自购或外采", "不含在基础技术交付中"],
  ["独立测试", "第三方测试", "客户自购或外采", "按实际范围"],
  ["云资源", "服务器、数据库、存储", "客户自购", "按实际发生"],
  ["通信服务", "短信、邮件、推送", "客户自购", "按实际发生"],
  ["平台服务", "地图、支付、模型调用", "客户自购", "按实际发生"],
  ["账号证书", "应用商店、域名、SSL", "客户自购", "按实际发生"],
  ["差旅", "现场交通与住宿", "实报实销", "现场工作另计人日"],
].map(([category, item, supplyMode, notes]) => ({
  id: newId(),
  category,
  item,
  supplyMode,
  costAmount: 0,
  customerAmount: 0,
  included: true,
  notes,
}));

export function createProject(name = "示例会员服务系统"): PricingProject {
  const now = new Date().toISOString();
  const date = now.slice(0, 10);
  return {
    id: newId(),
    quoteNumber: `HZ-${date.replaceAll("-", "")}-001`,
    clientName: "示例客户（请替换）",
    projectName: name,
    description: "用于记录项目背景、核心目标、范围与验收前提。",
    contact: "请填写",
    quoteDate: date,
    demandStatus: "清晰",
    discoveryDays: 3,
    discountRate: 0,
    taxRate: 0,
    maintenanceOption: "无",
    notes: "",
    wbsItems: seedWbs(),
    addonItems: seedAddons(),
    monthlySupport: { month: date.slice(0, 7), usedDays: 22, extraHours: 0, specialFactor: 1 },
    createdAt: now,
    updatedAt: now,
  };
}

export function createBlankWbsItem(): WbsItem {
  return {
    id: newId(),
    module: "",
    feature: "",
    phase: "开发构建",
    platform: "后端/API/数据库",
    estimateBasis: "编码人日",
    quickSize: "手动",
    inputDays: 0,
    complexity: "常规业务",
    deliveryFactors: [],
    notes: "",
  };
}

export function createBlankAddon(): AddonItem {
  return {
    id: newId(),
    category: "其他",
    item: "",
    supplyMode: "客户自购",
    costAmount: 0,
    customerAmount: 0,
    included: false,
    notes: "",
  };
}
